<script setup lang="ts">
// Vue imports
import { computed, onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'

// Module imports
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { statsModule } from '@/store/stats/module'
import { studentModule } from '@/store/student/module'

// UIKit imports
import UIKit from '@pocketprep/ui-kit'
const { Icon } = UIKit

// Vue router
const router = useRouter()
const route = useRoute()

/**
 * Refs
 */

const showVersionDropdown = ref(false)
const isLoading = ref(false)

/**
 * Computed properties
 */

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const currentExamMetadataId = computed(() => {
    return examModule.getters.getCurrentExamMetadata()?.objectId
})

const currentExamGuid = computed(() => {
    const currentGroupExam = examModule.getters.getCurrentGroupExam()
    return currentGroupExam && examMetadata.value[currentGroupExam.examMetadata.objectId]?.examGuid || ''
})

const currentExamVersion = computed(() => {
    const currentVersions = currentExamVersions.value
    return currentVersions.find(cev => cev.objectId === currentExamMetadataId.value) || currentVersions[0]
})

const currentExamVersions = computed(() => {
    return examModule.getters.getSortedExamMetadataForGuid(currentExamGuid.value).filter(em =>
        !examModule.getters.getIsExamMetadataRemoved(em)
    )
})

const examSwitchEnabled = computed(() => {
    return currentExamVersions.value.length > 1
})

const showExamVersionsDropdown = computed(() => {
    return showVersionDropdown.value && examSwitchEnabled.value
})

const currentGroup = computed(() => {
    return groupModule.getters.getCurrentGroup()
})

const currentStudent = computed(() => {
    return studentModule.getters.getCurrentStudent()
})

/**
 * Functions
 */

const selectExamVersion = async (versionObjectId: string) => {
    const currentGroupId = currentGroup.value?.objectId
    if (currentGroupId && currentExamMetadataId.value !== versionObjectId) {
        showVersionDropdown.value = false
        isLoading.value = true

        // Find the new exam version's group within the current groupCompositeKey's groups
        const currentGroupCompositeKey = groupModule.getters.getGroupCompositeKeyForGroupId(currentGroupId)
        const groupsInCompositeGroup = groupModule.getters
            .getGroupsByGroupCompositeKey()[currentGroupCompositeKey || '']
        const targetOrgExamId = examModule.getters.getOrgExamForExamMetadataId(versionObjectId)?.objectId
        const orgGroupId = groupsInCompositeGroup?.find(group => {
            return group.orgExam.objectId === targetOrgExamId
        })?.objectId

        if (orgGroupId) {
            statsModule.actions.resetGroupAggStats()
            statsModule.actions.resetStudentStats()
            statsModule.actions.resetStudentAggStats()

            // Either stats or content and may or may not have a student
            const isStatsRoute = route.path.search('/stats/') > -1
            const routeName = `${currentStudent.value ? 'student' : 'group'}-${isStatsRoute ? 'stats' : 'content'}`

            // Set the current exam version
            await examModule.actions.updateCurrentExamMetadataId(versionObjectId)

            router.push({
                name: routeName,
                params: {
                    groupId: orgGroupId,
                    studentId: currentStudent.value?.objectId,
                },
                query: { ...route.query },
            })
        }
        isLoading.value = false
    }
}

/**
 * Mounted
 */

onMounted(async () => {
    isLoading.value = true

    await Promise.all([
        groupModule.actions.fetchCurrentGroup(),
        examModule.actions.fetchCurrentGroupExam(),
        examModule.actions.fetchExamMetadata(),
        examModule.actions.fetchCurrentOrgExams(),
        studentModule.actions.fetchCurrentStudent(),
        groupModule.actions.fetchCurrentOrgGroups(),
        statsModule.actions.fetchCurrentStudentStats(),
        statsModule.actions.fetchCurrentGroupAggStats(),
    ])

    isLoading.value = false
})
</script>

<template>
    <div
        class="exam-version-dropdown"
        v-if="currentExamVersion"
    >
        <span
            v-if="currentExamVersion"
            class="exam-version-dropdown__exam-name"
            :class="{
                'exam-version-dropdown__exam-name--multiple': examSwitchEnabled
            }"
            :tabindex="currentExamVersions.length > 1 ? 0 : -1"
            @click="showVersionDropdown = !showVersionDropdown"
            @blur="showVersionDropdown = false"
        >
            {{ currentExamVersion.releaseInfo.name }}
            <Icon
                v-if="examSwitchEnabled"
                class="exam-version-dropdown__versions-dropdown-arrow"
                :class="{
                    'exam-version-dropdown__versions-dropdown-arrow--up': showVersionDropdown,
                }"
                type="accordionArrow"
            />
        </span>
        <ul
            v-if="showExamVersionsDropdown"
            class="exam-version-dropdown__versions-dropdown"
        >
            <li
                v-for="version in currentExamVersions"
                class="exam-version-dropdown__versions-dropdown-item"
                :key="version.objectId"
                :class="{
                    'exam-version-dropdown__versions-dropdown-item--selected':
                        version.objectId === currentExamVersion?.objectId
                }"
                @mousedown.prevent="selectExamVersion(version.objectId)"
                @click="selectExamVersion(version.objectId)"
            >
                <div>{{ version.releaseInfo.name }}</div>
            </li>
        </ul>
    </div>
</template>

<style lang="scss" scoped>
.exam-version-dropdown {
    &__exam-name {
        color: $slate;
        margin-bottom: 24px;
        font-size: 14px;
        line-height: 19px;

        &--multiple {
            color: $brand-blue;
            font-weight: 600;

            &:hover {
                color: $brand-blue-hover;
                cursor: pointer;
            }
        }
    }

    &__versions-dropdown {
        list-style: none;
        margin: 0;
        padding: 0;
        box-shadow: 0 6px 22px 0 rgb(31, 53, 69, 0.1);
        border-radius: 8px;
        position: absolute;
        top: 25px;
        left: -20px;
        z-index: 10;
        background: $white;
    }

    &__versions-dropdown-item {
        height: 43px;
        border-bottom: 1px solid $gray-divider;
        font-size: 14px;
        line-height: 43px;
        letter-spacing: 0.1px;
        padding: 0 20px;
        cursor: pointer;
        box-sizing: border-box;
        color: $brand-black;

        &:last-child {
            border-bottom: 0;
        }

        &--selected {
            font-weight: 600;
            cursor: default;
        }

        &:hover:not(.selected) {
            background: $gray-background;
            border-radius: 8px;
        }
    }

    &__versions-dropdown-arrow {
        display: inline-block;
        width: 13px;
        height: 7px;
        transition: 0.3s transform;
        color: $brand-blue;
        &--up {
            transform: rotate(180deg);
        }

        &:hover {
            color: $brand-blue-hover;
            cursor: pointer;
        }
    }
}
</style>
